import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { isNative, nativePlatform } from "@/lib/session";
import { api, getAnonId } from "@/lib/api";
import { useUser } from "@/store/user";
import {
  startGoogleNative,
  signInWithAppleNative,
  finishLogin,
  isAppleCancel,
} from "@/lib/nativeAuth";

const LOGIN_ERRORS = {
  1: "تعذّر إكمال تسجيل الدخول. يرجى المحاولة مجدّدًا.",
  google: "تعذّر إكمال تسجيل الدخول بحساب Google. يرجى المحاولة مجدّدًا.",
  google_cancel: "أُلغي تسجيل الدخول بحساب Google. يمكنك المحاولة مجدّدًا متى شئت.",
  session: "تمّ التحقّق من حسابك لكن تعذّر حفظ الجلسة على هذا الجهاز. يرجى المحاولة مجدّدًا.",
  apple: "تعذّر إكمال تسجيل الدخول بحساب Apple. يرجى المحاولة مجدّدًا.",
  apple_cancel: "أُلغي تسجيل الدخول بحساب Apple.",
  apple_state: "انتهت مهلة تسجيل الدخول بحساب Apple أو فُتح من نافذة أخرى. يرجى المحاولة مجدّدًا.",
  apple_config: "تسجيل الدخول بحساب Apple غير مُفعَّل على الموقع حاليًا.",
};

const IS_IOS_APP = nativePlatform === "ios";
const randomHex = () => Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");

// Web: full-page Sign in with Apple (form_post to our backend, same origin).
function startAppleWeb(servicesId) {
  const state = randomHex();
  const nonce = randomHex();
  document.cookie = `apple_oauth=${state}.${nonce}.${getAnonId()}; Path=/api/auth/apple; Max-Age=600; Secure; SameSite=None`;
  const q = new URLSearchParams({
    client_id: servicesId,
    redirect_uri: `${window.location.origin}/api/auth/apple/web-callback`,
    response_type: "code id_token",
    response_mode: "form_post",
    scope: "name email",
    state,
    nonce,
  });
  window.location.href = `https://appleid.apple.com/auth/authorize?${q}`;
}

/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useUser();
  const err = params.get("err");
  const pending = params.get("pending") === "1";
  const [consent, setConsent] = useState(false);
  const [showConsentWarning, setShowConsentWarning] = useState(false);
  const [appleBusy, setAppleBusy] = useState(false);
  const [appleError, setAppleError] = useState("");
  const [googleBusy, setGoogleBusy] = useState(false);
  const [webAppleId, setWebAppleId] = useState(null);
  const showApple = IS_IOS_APP || (!isNative && !!webAppleId);

  // Website shows Apple only once the backend has a Services ID configured.
  useEffect(() => {
    if (isNative) return;
    api.appleConfig().then((c) => setWebAppleId(c?.web_enabled ? c.services_id : null)).catch(() => null);
  }, []);

  const startApple = async () => {
    setAppleError("");
    if (!consent) {
      setShowConsentWarning(true);
      return;
    }
    setAppleBusy(true);
    try {
      if (nativePlatform === "ios") {
        const res = await signInWithAppleNative();
        const me = await finishLogin(res);
        await refresh(me);
        navigate("/", { replace: true });
        return;
      }
      startAppleWeb(webAppleId);
      return;
    } catch (e) {
      if (isAppleCancel(e)) setAppleError("أُلغي تسجيل الدخول بحساب Apple.");
      else if (e?.code === "session") setAppleError(LOGIN_ERRORS.session);
      else setAppleError(e?.response?.data?.detail || "تعذّر تسجيل الدخول بحساب Apple. يرجى المحاولة مجدّدًا.");
    } finally {
      setAppleBusy(false);
    }
  };

  const startLogin = async () => {
    if (!consent) {
      setShowConsentWarning(true);
      return;
    }
    if (isNative) {
      // Google blocks OAuth inside embedded WebViews → use the system browser.
      setGoogleBusy(true);
      try {
        await startGoogleNative();
      } catch (_) {
        navigate("/login?err=google", { replace: true });
      } finally {
        setGoogleBusy(false);
      }
      return;
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  };

  const goGuest = () => {
    if (!consent) {
      setShowConsentWarning(true);
      return;
    }
    navigate("/");
  };

  const toggleConsent = (e) => {
    setConsent(e.target.checked);
    if (e.target.checked) setShowConsentWarning(false);
  };

  return (
    <div
      className="shell"
      data-testid="login-page"
      style={{ display: "grid", placeItems: "center", minHeight: "80vh" }}
    >
      <div className="panel" style={{ maxWidth: 460, width: "100%", textAlign: "center" }}>
        <div
          className="mark"
          style={{
            width: 64,
            height: 64,
            borderRadius: 18,
            margin: "0 auto 12px",
            display: "grid",
            placeItems: "center",
            background: "var(--grad-gold)",
            color: "#20133a",
            fontFamily: "'Amiri', serif",
            fontWeight: 900,
            fontSize: 32,
          }}
        >
          ب
        </div>
        <h1 style={{ fontFamily: "'Amiri', serif", margin: "10px 0 6px", fontSize: 30 }}>
          مرحبًا بك في{" "}
          <span style={{ background: "var(--grad-gold)", WebkitBackgroundClip: "text", color: "transparent" }}>
            بَلاغة
          </span>
        </h1>
        <p className="dim" style={{ lineHeight: 1.9 }}>
          سجّل دخولك ليُحفَظ تقدّمك وشاراتك بأمان وتُزامِنها عبر أجهزتك.
        </p>

        {err && !pending && (
          <div className="hint" style={{ color: "#ff7aa8", marginBottom: 8 }} data-testid="login-error" role="alert">
            {LOGIN_ERRORS[err] || LOGIN_ERRORS[1]}
          </div>
        )}
        {pending && (
          <div className="hint" style={{ marginBottom: 8 }} data-testid="login-pending">
            جارٍ إكمال تسجيل الدخول…
          </div>
        )}

        <label
          className="consent-row"
          data-testid="consent-row"
          style={{ display: "flex", alignItems: "flex-start", gap: 10, textAlign: "right", marginTop: 18, marginBottom: 6, cursor: "pointer" }}
        >
          <input
            type="checkbox"
            checked={consent}
            onChange={toggleConsent}
            data-testid="consent-checkbox"
            className="consent-checkbox"
            aria-required="true"
          />
          <span style={{ fontSize: 13, lineHeight: 1.85, color: "var(--text-dim)" }}>
            أُوافق على{" "}
            <Link to="/privacy" data-testid="consent-privacy-link" className="legal-link">
              سياسة الخصوصيّة
            </Link>{" "}
            و
            <Link to="/terms" data-testid="consent-terms-link" className="legal-link">
              شروط الاستخدام
            </Link>
            .
          </span>
        </label>

        {showConsentWarning && !consent && (
          <div
            className="hint"
            data-testid="consent-warning"
            style={{ color: "#ff7aa8", fontSize: 13, marginTop: 6, marginBottom: 4 }}
          >
            يجب الموافقة على سياسة الخصوصيّة وشروط الاستخدام للمتابعة.
          </div>
        )}

        <button
          data-testid="google-login-btn"
          className="btn btn-gold"
          onClick={startLogin}
          disabled={googleBusy || pending}
          aria-disabled={!consent || googleBusy || pending}
          style={{
            width: "100%",
            justifyContent: "center",
            marginTop: 12,
            opacity: consent ? 1 : 0.55,
            cursor: consent ? "pointer" : "not-allowed",
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginLeft: 4 }}>
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0012 23z"/>
            <path fill="#FBBC04" d="M5.84 14.12A6.6 6.6 0 015.5 12c0-.74.13-1.45.34-2.12V7.04H2.18A11 11 0 001 12c0 1.78.43 3.46 1.18 4.96l3.66-2.84z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
          </svg>
          تسجيل الدخول بحساب Google
        </button>

        {/* Sign in with Apple (Apple App Store requirement) */}
        {showApple && (
        <button
          data-testid="apple-login-btn"
          className="btn"
          onClick={startApple}
          aria-disabled={!consent || appleBusy}
          disabled={appleBusy}
          style={{
            width: "100%",
            justifyContent: "center",
            marginTop: 10,
            background: "#000",
            color: "#fff",
            border: "1px solid #444",
            opacity: consent && !appleBusy ? 1 : 0.55,
            cursor: consent && !appleBusy ? "pointer" : "not-allowed",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff" style={{ marginLeft: 6 }}>
            <path d="M16.365 1.43c0 1.14-.453 2.21-1.198 2.99-.829.866-2.184 1.541-3.31 1.443-.14-1.124.394-2.292 1.13-3.045.83-.85 2.236-1.494 3.378-1.388zM20.94 17.51c-.522 1.16-.77 1.683-1.444 2.713-.94 1.435-2.27 3.224-3.918 3.238-1.463.014-1.84-.961-3.826-.951-1.985.01-2.4.967-3.864.953-1.647-.015-2.907-1.63-3.846-3.066-2.63-4.018-2.91-8.731-1.285-11.235 1.156-1.78 2.98-2.823 4.696-2.823 1.745 0 2.842 .961 4.285.961 1.4 0 2.253-.962 4.27-.962 1.524 0 3.138.831 4.29 2.27-3.77 2.07-3.16 7.474.642 8.902z"/>
          </svg>
          متابعة بحساب Apple
        </button>
        )}

        {appleError && (
          <div
            className="hint"
            data-testid="apple-error"
            style={{ color: "#ff7aa8", fontSize: 12, marginTop: 6 }}
          >
            {appleError}
          </div>
        )}

        <div className="row" style={{ justifyContent: "center", marginTop: 14 }}>
          <button
            data-testid="guest-btn"
            className="btn btn-outline"
            onClick={goGuest}
            aria-disabled={!consent}
            style={{ opacity: consent ? 1 : 0.55, cursor: consent ? "pointer" : "not-allowed" }}
          >
            متابعة كزائر
          </button>
        </div>

        <div className="muted" style={{ fontSize: 12, marginTop: 18, lineHeight: 1.7 }}>
          باستمرارك تُوافق على{" "}
          <Link to="/terms" className="legal-link">شروط الاستخدام</Link> و
          <Link to="/privacy" className="legal-link">سياسة الخصوصيّة</Link>.
        </div>
      </div>
    </div>
  );
}
